// Updated: 2026-08-21 09:14:00 CDT
// Purpose: Resolve only current-pipeline, current-voice TED/neutral audio and reject obsolete cached speech artifacts.

import type { Json } from "@/integrations/supabase/types";
import {
  AUDIO_PIPELINE_REVISION,
  neutralizeSentenceAudioBlock,
  sentenceAudioVariantType,
  type SentenceAudioBlock,
  type SentenceAudioVariantType,
} from "@/utils/audioBlockMap";

const SHA256_PATTERN = /^[0-9a-f]{64}$/u;

export interface StoredScreenplayAudioBlock {
  artifact_id: string | null;
  audio_revision: number;
  audio_status: string;
  block_type: string;
  content_revision: number;
  generation_text: string;
  input_hash: string;
  pipeline_revision: string;
  sentence_index: number;
  source_sequence_index: number;
  source_text: string;
  stable_key: string;
  synthesis_options: Json;
  updated_at: string;
  voice_id: string | null;
}

export interface StoredScreenplayAudioVariant {
  artifact_id: string | null;
  audio_status: string;
  block_type: string;
  generation_text: string;
  input_hash: string;
  pipeline_revision: string;
  sentence_index: number;
  source_sequence_index: number;
  source_text: string;
  stable_key: string;
  synthesis_options: Json;
  updated_at: string;
  variant_type: SentenceAudioVariantType;
  voice_id: string | null;
}

export interface SentencePlaybackSource {
  block: SentenceAudioBlock;
  storagePath: string | null;
}

const canonicalValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(canonicalValue);

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, child]) => [key, canonicalValue(child)]),
    );
  }

  return value;
};

export const canonicalJson = (value: unknown): string =>
  JSON.stringify(canonicalValue(value));

const equivalentJson = (left: unknown, right: unknown) =>
  canonicalJson(left) === canonicalJson(right);

const artifactMatchesVoiceRevision = (
  artifactUpdatedAt: string,
  voiceUpdatedAt: string | undefined,
): boolean => {
  if (!voiceUpdatedAt) return false;

  const artifactRevision = Date.parse(artifactUpdatedAt);
  const voiceRevision = Date.parse(voiceUpdatedAt);
  return Number.isFinite(artifactRevision) &&
    Number.isFinite(voiceRevision) &&
    artifactRevision >= voiceRevision;
};

export const isReusablePlaybackArtifact = (
  requested: SentenceAudioBlock,
  stored: StoredScreenplayAudioBlock,
  voiceUpdatedAt: string | undefined,
): boolean =>
  stored.stable_key === requested.id &&
  stored.audio_status === "ready" &&
  Boolean(stored.artifact_id) &&
  stored.audio_revision === stored.content_revision &&
  stored.source_sequence_index === requested.originalOrder &&
  stored.sentence_index === requested.sentenceIndex &&
  stored.block_type === requested.type &&
  stored.source_text === requested.sourceText &&
  stored.generation_text === requested.text &&
  stored.voice_id === requested.voiceId &&
  artifactMatchesVoiceRevision(stored.updated_at, voiceUpdatedAt) &&
  stored.pipeline_revision === AUDIO_PIPELINE_REVISION &&
  SHA256_PATTERN.test(stored.input_hash) &&
  equivalentJson(stored.synthesis_options, requested.synthesisOptions);

export const isReusablePlaybackVariant = (
  requested: SentenceAudioBlock,
  stored: StoredScreenplayAudioVariant,
  voiceUpdatedAt: string | undefined,
): boolean =>
  stored.stable_key === requested.id &&
  stored.variant_type === sentenceAudioVariantType(requested) &&
  stored.audio_status === "ready" &&
  Boolean(stored.artifact_id) &&
  stored.source_sequence_index === requested.originalOrder &&
  stored.sentence_index === requested.sentenceIndex &&
  stored.block_type === requested.type &&
  stored.source_text === requested.sourceText &&
  stored.generation_text === requested.text &&
  stored.voice_id === requested.voiceId &&
  artifactMatchesVoiceRevision(stored.updated_at, voiceUpdatedAt) &&
  stored.pipeline_revision === AUDIO_PIPELINE_REVISION &&
  SHA256_PATTERN.test(stored.input_hash) &&
  equivalentJson(stored.synthesis_options, requested.synthesisOptions);

export const resolveSentencePlaybackSources = (
  requestedBlocks: SentenceAudioBlock[],
  storedBlocks: StoredScreenplayAudioBlock[],
  userId: string,
  storedVariants: StoredScreenplayAudioVariant[] = [],
  voiceRevisions: ReadonlyMap<string, string> = new Map(),
): SentencePlaybackSource[] => {
  const storedByKey = new Map(
    storedBlocks.map((block) => [block.stable_key, block]),
  );
  const variantByKey = new Map(
    storedVariants.map((variant) => [
      `${variant.stable_key}:${variant.variant_type}`,
      variant,
    ]),
  );

  return requestedBlocks.map((block) => {
    const voiceUpdatedAt = voiceRevisions.get(block.voiceId);
    const requestedType = sentenceAudioVariantType(block);
    const exactVariant = variantByKey.get(`${block.id}:${requestedType}`);
    if (
      exactVariant &&
      isReusablePlaybackVariant(block, exactVariant, voiceUpdatedAt)
    ) {
      return {
        block,
        storagePath: `${userId}/block-artifacts/${exactVariant.input_hash}.wav`,
      };
    }

    const neutralBlock = neutralizeSentenceAudioBlock(block);
    const neutralVariant = variantByKey.get(`${block.id}:neutral`);
    if (
      neutralVariant &&
      isReusablePlaybackVariant(neutralBlock, neutralVariant, voiceUpdatedAt)
    ) {
      return {
        block,
        storagePath: `${userId}/block-artifacts/${neutralVariant.input_hash}.wav`,
      };
    }

    const stored = storedByKey.get(block.id);
    const exactLegacy = stored && isReusablePlaybackArtifact(block, stored, voiceUpdatedAt)
      ? stored
      : stored && isReusablePlaybackArtifact(neutralBlock, stored, voiceUpdatedAt)
        ? stored
        : null;
    const storagePath = exactLegacy
      ? `${userId}/block-artifacts/${exactLegacy.input_hash}.wav`
      : null;

    return { block, storagePath };
  });
};
